import React, { useEffect, useState } from "react";
import {
  auditBoq,
  buildProcurementSheet,
  goToCell,
  listChildSheetNames,
  scaleQuantities,
  createVolumeSheet,
  applyVolumeSheet,
  buildRfqSheet,
  buildScheduleSheet,
} from "../excel/boqTools";
import { fetchPriceImpact, applyPriceImpact, fetchRfq } from "../api/productTablesApi";
import { updateProject } from "../api/projectsApi";
import { readBoqTable, normalizeBoqNumber } from "../excel/boq-table";
import { distributeSchedule } from "../excel/scheduleMath";
import { confirmDialog } from "../utils/confirmDialog";

const card = {
  background: "var(--surface-soft)", border: "1px solid var(--surface-border)",
  borderRadius: 12, padding: 14, marginBottom: 14,
};
const input = {
  padding: "7px 10px", borderRadius: 8, fontSize: 13, boxSizing: "border-box",
  border: "1px solid var(--field-border)", background: "var(--field-bg)", color: "var(--text-primary)",
};
const SEV = {
  error: { color: "#fb7185", label: "შეცდომა" },
  warn: { color: "#f59e0b", label: "გაფრთხილება" },
  info: { color: "#38bdf8", label: "ინფო" },
};

function Title({ icon, title, hint }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 15, fontWeight: 700 }}>{icon} {title}</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 3, lineHeight: 1.5 }}>{hint}</div>
    </div>
  );
}

export default function BoqToolsPage({ onBack, projects = [], token }) {
  const project = (() => {
    try {
      const id = window.localStorage.getItem("boq.activeProjectId");
      return projects.find((p) => String(p.id) === String(id)) || null;
    } catch { return null; }
  })();

  // ── RFQ ──
  const [rfqMsg, setRfqMsg] = useState("");
  const [rfqBusy, setRfqBusy] = useState(false);
  // ── Payment schedule ──
  const scheduleKey = project ? `boq.schedule.${project.id}` : null;
  const [sched, setSched] = useState(() => {
    const base = { start: new Date().toISOString().slice(0, 7), advance: 0, chapters: [] };
    try {
      const saved = scheduleKey && window.localStorage.getItem(scheduleKey);
      return { ...base, ...(saved ? JSON.parse(saved) : project?.cost_config?.schedule || {}) };
    } catch { return base; }
  });
  const [schedMsg, setSchedMsg] = useState("");
  // ── Price impact ──
  const [impact, setImpact] = useState(null);
  const [impactBusy, setImpactBusy] = useState(false);
  const [impactMsg, setImpactMsg] = useState("");
  // ── Volume statement ──
  const [volMsg, setVolMsg] = useState("");
  const [volSkipped, setVolSkipped] = useState([]);
  // ── Audit ──
  const [audit, setAudit] = useState(null);
  const [auditBusy, setAuditBusy] = useState(false);
  // ── Procurement ──
  const [procMsg, setProcMsg] = useState("");
  const [procBusy, setProcBusy] = useState(false);
  // ── Scale ──
  const [sheetNames, setSheetNames] = useState([]);
  const [picked, setPicked] = useState([]);
  const [oldArea, setOldArea] = useState("");
  const [newArea, setNewArea] = useState("");
  const [factorText, setFactorText] = useState("");
  const [scaleMsg, setScaleMsg] = useState("");
  const [error, setError] = useState("");

  const loadSheets = () =>
    listChildSheetNames()
      .then((names) => { setSheetNames(names); setPicked(names); })
      .catch((e) => setError(e?.message || "ფურცლების წაკითხვა ვერ მოხერხდა"));
  useEffect(() => { loadSheets(); }, []);

  const factor = factorText
    ? Number(String(factorText).replace(",", "."))
    : Number(oldArea) > 0 && Number(newArea) > 0 ? Number(newArea) / Number(oldArea) : 0;

  const versionId = (() => {
    try { return window.localStorage.getItem("boq.activeVersionId") || null; } catch { return null; }
  })();

  const runRfq = async () => {
    setError(""); setRfqMsg(""); setRfqBusy(true);
    try {
      if (!project) throw new Error("ჯერ გახსენი პროექტი და BOQ ჩატვირთე.");
      const rfq = await fetchRfq(token, project.id, versionId);
      const r = await buildRfqSheet(rfq, project.name);
      setRfqMsg(`✓ ფურცელი „მოთხოვნა მომწოდებლებს“ შეიქმნა: ${r.suppliers} მომწოდებელი${r.unassigned ? `, ${r.unassigned} პოზიცია მიუბმელია` : ""}.`);
    } catch (e) { setError(e?.message || "მოთხოვნა ვერ შეიქმნა"); }
    finally { setRfqBusy(false); }
  };

  const loadScheduleChapters = async () => {
    setError(""); setSchedMsg("");
    try {
      const sheetName = (() => { try { return window.localStorage.getItem("boq.activeRootSheet"); } catch { return null; } })();
      if (!sheetName) throw new Error("ჯერ გახსენი პროექტის BOQ ჩანართი.");
      const rows = (await readBoqTable(sheetName)).filter((r) => !normalizeBoqNumber(r.number).includes("."));
      if (!rows.length) throw new Error("TOTAL ფურცელზე თავები ვერ მოიძებნა.");
      const prev = new Map((sched.chapters || []).map((c) => [c.number, c]));
      setSched((s0) => ({
        ...s0,
        chapters: rows.map((r, i) => ({
          number: normalizeBoqNumber(r.number) || String(i + 1),
          name: r.description,
          total: r.totalPrice,
          start: prev.get(normalizeBoqNumber(r.number))?.start ?? i + 1,
          dur: prev.get(normalizeBoqNumber(r.number))?.dur ?? 2,
        })),
      }));
    } catch (e) { setError(e?.message || "თავების წაკითხვა ვერ მოხერხდა"); }
  };

  const setChapterField = (idx, field, value) =>
    setSched((s0) => ({ ...s0, chapters: s0.chapters.map((c, i) => (i === idx ? { ...c, [field]: value } : c)) }));

  const runSchedule = async () => {
    setError(""); setSchedMsg("");
    try {
      const r = await buildScheduleSheet({
        projectName: project?.name || "", startMonth: sched.start,
        advancePct: sched.advance, chapters: sched.chapters,
      });
      setSchedMsg(`✓ ფურცელი „გრაფიკი“ შეიქმნა (${r.months} თვე).`);
      if (scheduleKey) {
        try { window.localStorage.setItem(scheduleKey, JSON.stringify(sched)); } catch { /* ignore */ }
        if (token && project) {
          // Share the plan with the team (stored in the project's settings); best effort.
          updateProject(token, project.id, { cost_config: { ...(project.cost_config || {}), schedule: sched } }).catch(() => {});
        }
      }
    } catch (e) { setError(e?.message || "გრაფიკი ვერ შეიქმნა"); }
  };

  const schedPreview = distributeSchedule(sched.chapters, sched.advance);

  const runImpact = async () => {
    setError(""); setImpactMsg(""); setImpactBusy(true);
    try {
      if (!project) throw new Error("ჯერ გახსენი პროექტი და BOQ ჩატვირთე.");
      setImpact(await fetchPriceImpact(token, project.id, versionId));
    } catch (e) { setError(e?.message || "ანგარიში ვერ შეიქმნა"); }
    finally { setImpactBusy(false); }
  };

  const applyImpact = async () => {
    if (!impact?.outdated) return;
    const ok = await confirmDialog(
      `${impact.outdated} ფასი განახლდება კატალოგის მიმდინარე ფასებზე (პირდაპირი ხარჯი ${impact.impact_total >= 0 ? "+" : ""}${Math.round(impact.impact_total).toLocaleString("ka-GE")} ₾). ` +
      "ამჟამინდელი მდგომარეობა ჯერ ავტომატურად შეინახება (ისტორიიდან აღდგენადია). გაგრძელება?"
    );
    if (!ok) return;
    setError(""); setImpactBusy(true);
    try {
      const res = await applyPriceImpact(token, project.id, impact.version_id);
      setImpactMsg(`✓ განახლდა ${res.updated} ფასი. BOQ ჩანართში დააჭირე Load, შემდეგ Save.`);
      setImpact(await fetchPriceImpact(token, project.id, versionId));
    } catch (e) { setError(e?.message || "განახლება ვერ მოხერხდა"); }
    finally { setImpactBusy(false); }
  };

  const runCreateVolumes = async () => {
    setError(""); setVolMsg(""); setVolSkipped([]);
    try {
      const r = await createVolumeSheet();
      setVolMsg(`✓ ფურცელი „მოცულობათა უწყისი“ შეიქმნა (${r.rows} სამუშაო). ჩაწერე ახალი მოცულობები ყვითელ სვეტში.`);
    } catch (e) { setError(e?.message || "უწყისის შექმნა ვერ მოხერხდა"); }
  };

  const runApplyVolumes = async () => {
    setError(""); setVolMsg(""); setVolSkipped([]);
    try {
      const r = await applyVolumeSheet();
      setVolMsg(`✓ შეიცვალა ${r.updated} მოცულობა, უცვლელი ${r.unchanged}${r.skipped.length ? `, გამოტოვებული ${r.skipped.length}` : ""}. ახლა დააჭირე BOQ-ში „შენახვა“.`);
      setVolSkipped(r.skipped);
    } catch (e) { setError(e?.message || "გამოყენება ვერ მოხერხდა"); }
  };

  const runAudit = async () => {
    setError(""); setAuditBusy(true);
    try { setAudit(await auditBoq()); }
    catch (e) { setError(e?.message || "შემოწმება ვერ მოხერხდა"); }
    finally { setAuditBusy(false); }
  };

  const runProcurement = async () => {
    setError(""); setProcMsg(""); setProcBusy(true);
    try {
      const res = await buildProcurementSheet({ projectName: project?.name || "" });
      setProcMsg(`✓ ფურცელი „მასალების უწყისი“ შეიქმნა: ${res.items} პოზიცია, ჯამი ${Math.round(res.total).toLocaleString("ka-GE")} ₾`);
    } catch (e) { setError(e?.message || "უწყისის შექმნა ვერ მოხერხდა"); }
    finally { setProcBusy(false); }
  };

  const runScale = async () => {
    setError(""); setScaleMsg("");
    try {
      const dry = await scaleQuantities({ factor, sheetNames: picked, dryRun: true });
      if (!dry.cells) { setScaleMsg("შესაცვლელი რაოდენობა ვერ მოიძებნა."); return; }
      const ok = await confirmDialog(
        `${dry.sheets} ფურცელზე ${dry.cells} რაოდენობა გამრავლდება ×${factor.toFixed(4)}-ზე` +
        (dry.skippedFormulas ? ` (ფორმულიანი ${dry.skippedFormulas} უჯრა გამოტოვდება)` : "") +
        ". გაგრძელება? (Excel-ში Ctrl+Z-ით შეიძლება გაუქმება)"
      );
      if (!ok) return;
      const res = await scaleQuantities({ factor, sheetNames: picked });
      setScaleMsg(`✓ შეიცვალა ${res.cells} უჯრა ${res.sheets} ფურცელზე. ახლა დააჭირე „შენახვა“ BOQ-ში.`);
    } catch (e) { setError(e?.message || "გადამრავლება ვერ მოხერხდა"); }
  };

  return (
    <div data-no-translate="true" style={{ minHeight: "100vh", background: "var(--page-gradient)", padding: "20px 16px", color: "var(--text-primary)" }}>
      <button className="link" onClick={onBack}>← უკან</button>
      <h2 style={{ marginTop: 16, marginBottom: 4 }}>🧰 ხელსაწყოები</h2>
      <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 16 }}>
        ხარჯთაღრიცხვის შემოწმება, შესყიდვების უწყისი და რაოდენობების გადამრავლება
      </p>
      {error && <div className="msg-error" style={{ marginBottom: 12 }}>{error}</div>}

      {/* ── Audit ── */}
      <div style={card}>
        <Title
          icon="✅" title="ხარჯთაღრიცხვის შემოწმება"
          hint="პოულობს უფასო ან ურაოდენობო სტრიქონებს, ერთეულის გარეშე პოზიციებს, დუბლიკატებს და ერთი და იგივე მასალის განსხვავებულ ფასებს."
        />
        <button className="primary" onClick={runAudit} disabled={auditBusy} style={{ padding: "8px 14px" }}>
          {auditBusy ? "მოწმდება..." : "შემოწმება"}
        </button>
        {audit && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10, fontSize: 12 }}>
              <span>{audit.sheetCount} ფურცელი · {audit.rowCount} პოზიცია</span>
              <span style={{ color: SEV.error.color, fontWeight: 700 }}>{audit.errors} შეცდომა</span>
              <span style={{ color: SEV.warn.color, fontWeight: 700 }}>{audit.warnings} გაფრთხილება</span>
            </div>
            {audit.issues.length === 0 && (
              <div style={{ fontSize: 13, color: "#34d399", fontWeight: 600 }}>✓ პრობლემა ვერ მოიძებნა</div>
            )}
            <div style={{ maxHeight: 340, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
              {audit.issues.slice(0, 200).map((it, i) => (
                <button
                  key={i}
                  onClick={() => goToCell(it.sheet, it.cell).catch((e) => setError(e?.message))}
                  title="უჯრაზე გადასვლა"
                  style={{
                    textAlign: "left", padding: "7px 10px", borderRadius: 8, cursor: "pointer",
                    border: "1px solid var(--surface-border)", borderLeft: `4px solid ${SEV[it.sev].color}`,
                    background: "var(--field-bg)", color: "var(--text-primary)", fontSize: 12,
                  }}
                >
                  <div>{it.msg}</div>
                  <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>{it.sheet} · {it.cell}</div>
                </button>
              ))}
              {audit.issues.length > 200 && (
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>ნაჩვენებია პირველი 200 / {audit.issues.length}</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Procurement ── */}
      <div style={card}>
        <Title
          icon="🧾" title="შესყიდვების უწყისი"
          hint="აჯამებს ყველა დეტალური ფურცლის მასალას, შრომას და ტექნიკას: ჯამური რაოდენობა, საშუალო/მინ/მაქს ფასი, წილი და ABC კლასი. მზადაა მომწოდებლებთან გასაგზავნად."
        />
        <button className="primary" onClick={runProcurement} disabled={procBusy} style={{ padding: "8px 14px" }}>
          {procBusy ? "იქმნება..." : "უწყისის შექმნა Excel-ში"}
        </button>
        {procMsg && <div style={{ marginTop: 10, fontSize: 12, color: "#34d399" }}>{procMsg}</div>}
      </div>

      {/* ── Price impact ── */}
      <div style={card}>
        <Title
          icon="💱" title="ფასების შედარება კატალოგთან"
          hint="ადარებს BOQ-ის რესურსების ფასებს (კოდით მიბმულს) მომწოდებლების დღევანდელ საუკეთესო ფასებს და აჩვენებს, რამდენით შეიცვლება პირდაპირი ხარჯი. განახლებამდე ავტომატურად იქმნება სარეზერვო შენახვა."
        />
        <button className="primary" onClick={runImpact} disabled={impactBusy || !token} style={{ padding: "8px 14px" }}>
          {impactBusy ? "..." : "შედარება"}
        </button>
        {impactMsg && <div style={{ marginTop: 10, fontSize: 12, color: "#34d399" }}>{impactMsg}</div>}
        {impact && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, marginBottom: 8, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <span>შესაბამისი: {impact.matched}</span>
              <span style={{ color: "var(--text-muted)" }}>მიუბმელი: {impact.unmatched}</span>
              <span style={{ color: impact.outdated ? "#f59e0b" : "#34d399", fontWeight: 700 }}>მოძველებული: {impact.outdated}</span>
              {impact.outdated > 0 && (
                <span style={{ fontWeight: 700, color: impact.impact_total > 0 ? "#fb7185" : "#34d399" }}>
                  ეფექტი: {impact.impact_total > 0 ? "+" : ""}{Math.round(impact.impact_total).toLocaleString("ka-GE")} ₾
                </span>
              )}
              {impact.no_offer > 0 && (
                <span style={{ color: "#f59e0b" }} title="პროდუქტი კატალოგშია, მაგრამ მიმდინარე ფასი არც ერთ მომწოდებელს არ აქვს">⚠ ფასი ვადაგასულია: {impact.no_offer}</span>
              )}
              {impact.expiring_soon > 0 && (
                <span style={{ color: "#f59e0b" }} title="საუკეთესო შეთავაზების ვადა 14 დღეში იწურება">⏳ იწურება 14 დღეში: {impact.expiring_soon}</span>
              )}
            </div>
            {impact.outdated === 0 && <div style={{ fontSize: 13, color: "#34d399", fontWeight: 600 }}>✓ ფასები აქტუალურია</div>}
            <div style={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
              {impact.items.slice(0, 40).map((it) => (
                <div key={it.row_id} style={{ padding: "6px 10px", borderRadius: 8, fontSize: 12, border: "1px solid var(--surface-border)", background: "var(--field-bg)" }}>
                  <div>{it.display_id} {it.name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                    {it.old_rate} → <strong style={{ color: it.new_rate > it.old_rate ? "#fb7185" : "#34d399" }}>{it.new_rate}</strong>
                    {" · "}რაოდ. {it.qty}{" · "}{it.impact > 0 ? "+" : ""}{Math.round(it.impact).toLocaleString("ka-GE")} ₾
                    {it.supplier_name ? ` · ${it.supplier_name}` : ""}
                  </div>
                </div>
              ))}
            </div>
            {impact.outdated > 0 && (
              <button className="primary" onClick={applyImpact} disabled={impactBusy} style={{ marginTop: 10, padding: "8px 14px" }}>
                ყველა {impact.outdated} ფასის განახლება
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── RFQ ── */}
      <div style={card}>
        <Title
          icon="📨" title="მოთხოვნა მომწოდებლებს"
          hint="აჯგუფებს BOQ-ის მასალებსა და ტექნიკას მომწოდებლის მიხედვით (საუკეთესო მიმდინარე შეთავაზებით) და ქმნის ფურცელს ფასის მოსათხოვნად: რაოდენობები, ბოლო ფასი და ცარიელი სვეტი „თქვენი ფასი“."
        />
        <button className="primary" onClick={runRfq} disabled={rfqBusy || !token} style={{ padding: "8px 14px" }}>
          {rfqBusy ? "..." : "მოთხოვნის ფურცლის შექმნა"}
        </button>
        {rfqMsg && <div style={{ marginTop: 10, fontSize: 12, color: "#34d399" }}>{rfqMsg}</div>}
      </div>

      {/* ── Payment schedule ── */}
      <div style={card}>
        <Title
          icon="📆" title="გადახდების გრაფიკი"
          hint="თავების ღირებულებას ანაწილებს თვეებზე (დაწყება და ხანგრძლივობა), ითვალისწინებს ავანსს და ქმნის Excel-ის ფურცელს კუმულაციური გადახდების გრაფიკით — დამკვეთისა და ბანკისთვის."
        />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
          <label style={{ fontSize: 12 }}>დაწყება{" "}
            <input type="month" value={sched.start} onChange={(e) => setSched((s0) => ({ ...s0, start: e.target.value }))} style={{ ...input, width: 140 }} />
          </label>
          <label style={{ fontSize: 12 }}>ავანსი %{" "}
            <input type="number" min="0" max="100" value={sched.advance} onChange={(e) => setSched((s0) => ({ ...s0, advance: e.target.value }))} style={{ ...input, width: 70 }} />
          </label>
          <button onClick={loadScheduleChapters} style={{ padding: "7px 12px", borderRadius: 8, fontSize: 12, cursor: "pointer", border: "1px solid var(--surface-border)", background: "var(--surface-soft)", color: "var(--text-primary)" }}>
            ↻ თავების ჩატვირთვა TOTAL-იდან
          </button>
        </div>
        {sched.chapters.length > 0 && (
          <>
            <div style={{ maxHeight: 220, overflowY: "auto", marginBottom: 8 }}>
              {sched.chapters.map((c, i) => (
                <div key={c.number} style={{ display: "flex", gap: 6, alignItems: "center", padding: "4px 0", fontSize: 12 }}>
                  <span style={{ width: 22, color: "var(--text-muted)" }}>{c.number}</span>
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.name}>{c.name}</span>
                  <span style={{ width: 80, textAlign: "right", color: "var(--text-muted)" }}>{Math.round(c.total).toLocaleString("ka-GE")}</span>
                  <input type="number" min="1" title="დაწყების თვე" value={c.start} onChange={(e) => setChapterField(i, "start", e.target.value)} style={{ ...input, width: 52, padding: "3px 6px" }} />
                  <input type="number" min="1" title="ხანგრძლივობა (თვე)" value={c.dur} onChange={(e) => setChapterField(i, "dur", e.target.value)} style={{ ...input, width: 52, padding: "3px 6px" }} />
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>
              სვეტები: თავი · ჯამი · დაწყების თვე · ხანგრძლივობა. სულ {schedPreview.months} თვე, ჯამი {Math.round(schedPreview.grand).toLocaleString("ka-GE")} ₾
            </div>
            <button className="primary" onClick={runSchedule} style={{ padding: "8px 14px" }}>გრაფიკის შექმნა Excel-ში</button>
          </>
        )}
        {schedMsg && <div style={{ marginTop: 10, fontSize: 12, color: "#34d399" }}>{schedMsg}</div>}
      </div>

      {/* ── Volume statement ── */}
      <div style={card}>
        <Title
          icon="📋" title="მოცულობათა უწყისი"
          hint="ქმნის ფურცელს ყველა სამუშაოს ჩამონათვალით. ჩაწერე ახალი მოცულობები და ერთი დაჭერით გადავა დეტალურ ფურცლებზე — რესურსები თავად გადაითვლება."
        />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="primary" onClick={runCreateVolumes} style={{ padding: "8px 14px" }}>1. უწყისის შექმნა</button>
          <button className="primary" onClick={runApplyVolumes} style={{ padding: "8px 14px" }}>2. მოცულობების გამოყენება</button>
        </div>
        {volMsg && <div style={{ marginTop: 10, fontSize: 12, color: "#34d399" }}>{volMsg}</div>}
        {volSkipped.length > 0 && (
          <div style={{ marginTop: 8, fontSize: 11, color: "#f59e0b" }}>
            {volSkipped.slice(0, 8).map((m, i) => <div key={i}>⚠ {m}</div>)}
          </div>
        )}
      </div>

      {/* ── Scale ── */}
      <div style={card}>
        <Title
          icon="📐" title="რაოდენობების გადამრავლება"
          hint="იგივე სამუშაო, სხვა ზომა: მიუთითე ძველი და ახალი ფართობი (ან პირდაპირ კოეფიციენტი) და ყველა რაოდენობა გადამრავლდება. ფორმულიანი უჯრები არ იცვლება."
        />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
          <input style={{ ...input, width: 110 }} type="number" min="0" placeholder="ძველი მ²" value={oldArea}
            onChange={(e) => { setOldArea(e.target.value); setFactorText(""); }} />
          <input style={{ ...input, width: 110 }} type="number" min="0" placeholder="ახალი მ²" value={newArea}
            onChange={(e) => { setNewArea(e.target.value); setFactorText(""); }} />
          <input style={{ ...input, width: 110 }} placeholder="ან კოეფიც." value={factorText}
            onChange={(e) => setFactorText(e.target.value)} />
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>
          კოეფიციენტი: <strong style={{ color: "var(--text-primary)" }}>{factor > 0 ? `×${factor.toFixed(4)}` : "—"}</strong>
        </div>
        <div style={{ maxHeight: 140, overflowY: "auto", border: "1px solid var(--surface-border)", borderRadius: 8, marginBottom: 8 }}>
          {sheetNames.length === 0 && (
            <div style={{ padding: 10, fontSize: 12, color: "var(--text-muted)" }}>დეტალური ფურცლები ვერ მოიძებნა.</div>
          )}
          {sheetNames.map((n) => (
            <label key={n} style={{ display: "flex", gap: 8, padding: "6px 10px", fontSize: 12, cursor: "pointer", alignItems: "center" }}>
              <input
                type="checkbox" checked={picked.includes(n)}
                onChange={() => setPicked((p) => (p.includes(n) ? p.filter((x) => x !== n) : [...p, n]))}
              />
              {n}
            </label>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="primary" onClick={runScale} disabled={!(factor > 0) || !picked.length} style={{ padding: "8px 14px" }}>
            გადამრავლება
          </button>
          <button onClick={() => setPicked(picked.length === sheetNames.length ? [] : sheetNames)}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid var(--surface-border)", background: "transparent", color: "var(--text-muted)", cursor: "pointer", fontSize: 12 }}>
            {picked.length === sheetNames.length ? "მოხსნა" : "ყველა"}
          </button>
          <button onClick={loadSheets}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid var(--surface-border)", background: "transparent", color: "var(--text-muted)", cursor: "pointer", fontSize: 12 }}>
            ↻ განახლება
          </button>
        </div>
        {scaleMsg && <div style={{ marginTop: 10, fontSize: 12, color: "#34d399" }}>{scaleMsg}</div>}
      </div>
    </div>
  );
}
